import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams, useNavigationType, UNSAFE_NavigationContext } from 'react-router-dom';

/**
 * @fileoverview Enhanced Compatibility layer for React Router v6
 * 
 * @description This module provides comprehensive backward compatibility with React Router v4/v5
 * to enable seamless migration without extensive refactoring of existing components.
 * It includes proper browser history management, back button support, and error handling.
 * 
 * @why Created during the React Router v6 migration to address breaking changes:
 * - v6 removed the withRouter HOC that many class components depend on
 * - v6 removed automatic injection of router props (history, location, match)
 * - v6 changed the history API and removed some methods like history.listen()
 * - v6 requires functional components and hooks, but this codebase has many class components
 * 
 * @migration This module enables a gradual migration strategy:
 * 1. Install this compatibility layer
 * 2. Replace React Router v4/v5 withRouter with this version
 * 3. Components continue working without code changes
 * 4. Gradually migrate individual components to v6 hooks when convenient
 * 
 * @author React Router v6 Migration Team
 * @since React Router v6 migration
 * @version 1.0.0
 */

/**
 * Global set of history listeners for cross-component communication
 * @type {Set<Function>}
 * @description Manages listeners that need to be notified of navigation changes.
 * This replaces the history.listen() functionality from React Router v4/v5.
 */
const historyListeners = new Set();

/**
 * Global set of navigation block handlers for preventing unwanted navigation
 * @type {Set<Function>}  
 * @description Manages functions that can block navigation with user confirmation.
 * This replaces the history.block() functionality from React Router v4/v5.
 */
const blockListeners = new Set();

/**
 * Custom hook that manages history listeners for React Router v6 compatibility
 * 
 * @description Created to bridge the gap between React Router v4/v5 history.listen()
 * functionality and React Router v6. In v4/v5, components could directly listen to
 * history changes via history.listen(). v6 removed this capability, so this hook
 * recreates that functionality by managing a global set of listeners that get
 * notified on location/navigation type changes.
 * 
 * @why This was necessary because many existing components in the codebase rely on
 * history.listen() to react to navigation changes. Rather than refactor all components
 * to use useLocation/useNavigationType directly, this provides a compatibility layer.
 * 
 * @example
 * // Used internally by withRouter HOC to enable history.listen() functionality
 * const Component = () => {
 *   useHistoryListener(); // Automatically notifies all registered listeners
 *   return <div>Component</div>;
 * };
 * 
 * @since React Router v6 migration
 */
export const useHistoryListener = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  
  useEffect(() => {
    historyListeners.forEach(listener => {
      try {
        listener(location, navigationType);
      } catch (error) {
        console.error('History listener error:', error);
      }
    });
  }, [location, navigationType]);
};

/**
 * Higher-Order Component that provides React Router v4/v5 compatibility for class components
 * 
 * @description This HOC was created to enable seamless migration from React Router v4/v5 to v6
 * without requiring extensive refactoring of existing class components. It injects router props
 * (history, location, match, params) that were automatically available in v4/v5 but removed in v6.
 * 
 * @why React Router v6 removed automatic injection of router props and the withRouter HOC.
 * Many existing class components in this codebase depend on these props for navigation and
 * route parameter access. Rather than converting all class components to functional components
 * with hooks, this provides a drop-in replacement that maintains the same API.
 * 
 * @param {React.ComponentType} Component - The component to wrap with router functionality
 * @returns {React.ComponentType} Enhanced component with router props injected
 * 
 * @props {Object} history - Navigation history object with methods like push, replace, goBack
 * @props {Object} location - Current location object with pathname, search, hash, state
 * @props {Object} match - Route match object with params, isExact, path, url (v4/v5 compatibility)
 * @props {Object} params - URL parameters object (shortcut to match.params)
 * @props {Function} navigate - React Router v6 navigate function
 * @props {Object} router - Combined router object containing history, location, match
 * 
 * @example
 * // Before (v4/v5): Component automatically received router props
 * class MyComponent extends Component {
 *   componentDidMount() {
 *     this.props.history.push('/new-route');
 *   }
 * }
 * export default withRouter(MyComponent);
 * 
 * // After (v6): Same usage, no changes needed to component code
 * class MyComponent extends Component {
 *   componentDidMount() {
 *     this.props.history.push('/new-route'); // Still works!
 *   }
 * }
 * export default withRouter(MyComponent);
 * 
 * @features
 * - Browser history management and back button support
 * - Navigation blocking with confirmation dialogs
 * - History listeners for responding to navigation changes
 * - Automatic cleanup of listeners on component unmount
 * - Enhanced navigation stack tracking
 * - Fallback navigation for empty history
 * 
 * @since React Router v6 migration
 */
export const withRouter = (Component) => {
  return React.memo((props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const navigationType = useNavigationType();
    const navigationContext = React.useContext(UNSAFE_NavigationContext);
    
    const navigationStack = useRef([]);
    const listenersRef = useRef(new Set());
    
    useEffect(() => {
      navigationStack.current.push(location.pathname);
      if (navigationStack.current.length > 50) {
        navigationStack.current.shift();
      }
    }, [location.pathname]);
    
    const history = useMemo(() => ({
      push: (to, state) => {
        if (typeof to === 'string') {
          navigate(to, { state });
        } else {
          navigate(to.pathname || to, { state: to.state || state });
        }
      },
      
      replace: (to, state) => {
        if (typeof to === 'string') {
          navigate(to, { state, replace: true });
        } else {
          navigate(to.pathname || to, { state: to.state || state, replace: true });
        }
      },
      
      go: (n) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      
      get length() {
        return window.history.length;
      },
      
      get action() {
        return navigationType;
      },
      
      location,
      
      createHref: (to) => {
        if (typeof to === 'string') return to;
        return to.pathname || '/';
      },
      
      listen: (listener) => {
        historyListeners.add(listener);
        listenersRef.current.add(listener);
        
        return () => {
          historyListeners.delete(listener);
          listenersRef.current.delete(listener);
        };
      },
      
      block: (blocker) => {
        const blockHandler = (tx) => {
          const shouldBlock = typeof blocker === 'function' 
            ? blocker(tx.location, tx.action) 
            : blocker;
            
          if (shouldBlock) {
            const message = typeof shouldBlock === 'string' 
              ? shouldBlock 
              : 'Are you sure you want to leave?';
              
            if (!window.confirm(message)) {
              tx.retry();
              return false;
            }
          }
          return true;
        };
        
        blockListeners.add(blockHandler);
        
        const handleBeforeUnload = (e) => {
          if (blocker) {
            e.preventDefault();
            e.returnValue = '';
          }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
          blockListeners.delete(blockHandler);
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      },
      
      canGoBack: () => navigationStack.current.length > 1,
      
      getPreviousLocation: () => {
        const stack = navigationStack.current;
        return stack.length > 1 ? stack[stack.length - 2] : null;
      },
      
      handleBrowserBack: (fallbackPath = '/') => {
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate(fallbackPath);
        }
      }
    }), [navigate, location, navigationType]);
    
    // Create match object for v4/v5 compatibility
    const match = useMemo(() => ({
      params,
      isExact: true,
      path: location.pathname,
      url: location.pathname,
    }), [params, location.pathname]);
    
    useHistoryListener();
    
    useEffect(() => {
      return () => {
        listenersRef.current.forEach(listener => {
          historyListeners.delete(listener);
        });
      };
    }, []);
    
    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={match}
        params={params}
        navigate={navigate}
        router={{ history, location, match }}
      />
    );
  });
};

/**
 * Custom hook that provides React Router v4/v5 compatibility for functional components
 * 
 * @description This hook was created to provide an alternative to the withRouter HOC for
 * functional components that need access to router functionality. It returns the same
 * router objects that were available in v4/v5 but using React Router v6 hooks under the hood.
 * 
 * @why While functional components can use individual hooks like useNavigate, useLocation, etc.,
 * some components were designed to expect the combined router objects from v4/v5. This hook
 * provides those same objects for components that need to maintain compatibility or when
 * you need to pass router objects to child components or callbacks.
 * 
 * @returns {Object} Router compatibility object
 * @returns {Object} returns.history - Navigation history object with v4/v5 compatible methods
 * @returns {Object} returns.location - Current location object
 * @returns {Object} returns.match - Route match object with params (v4/v5 compatibility)
 * @returns {Object} returns.params - URL parameters object
 * @returns {Function} returns.navigate - React Router v6 navigate function
 * 
 * @example
 * // Instead of using multiple hooks:
 * const navigate = useNavigate();
 * const location = useLocation();
 * const params = useParams();
 * 
 * // Use this hook for v4/v5 compatibility:
 * const { history, location, match, params, navigate } = useRouterCompat();
 * 
 * // Can pass router objects to callbacks or child components
 * const handleClick = () => {
 *   onNavigate(history, location); // Pass familiar objects
 * };
 * 
 * @note This hook provides a simpler history object compared to withRouter.
 * For advanced features like navigation blocking or stack tracking, use withRouter instead.
 * 
 * @since React Router v6 migration
 */
export const useRouterCompat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const navigationType = useNavigationType();
  
  const history = useMemo(() => ({
    push: (to, state) => navigate(to, { state }),
    replace: (to, state) => navigate(to, { state, replace: true }),
    go: (n) => navigate(n),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    length: window.history.length,
    action: navigationType,
    location,
    listen: (listener) => {
      historyListeners.add(listener);
      return () => historyListeners.delete(listener);
    },
    block: () => () => {},
    createHref: (to) => typeof to === 'string' ? to : to.pathname || '/',
  }), [navigate, location, navigationType]);
  
  const match = useMemo(() => ({
    params,
    isExact: true,
    path: location.pathname,
    url: location.pathname,
  }), [params, location.pathname]);
  
  return { history, location, match, params, navigate };
};

/**
 * React Error Boundary specifically designed to catch and handle navigation-related errors
 * 
 * @description This error boundary was created to provide graceful error handling during
 * the React Router v6 migration. Navigation errors can occur due to route mismatches,
 * invalid parameters, or compatibility issues between v4/v5 and v6 APIs. Instead of
 * crashing the entire application, this boundary catches navigation errors and provides
 * a user-friendly fallback UI.
 * 
 * @why During router migrations, navigation errors are common due to:
 * - Route parameter mismatches between old and new router versions
 * - Components trying to use deprecated router APIs
 * - Invalid route configurations or missing route definitions
 * - Hash routing vs browser routing conflicts
 * Rather than debugging each error individually, this boundary provides a safety net.
 * 
 * @class NavigationErrorBoundary
 * @extends {React.Component}
 * 
 * @example
 * // Wrap components that use router functionality
 * <NavigationErrorBoundary>
 *   <ComponentThatUsesRouter />
 * </NavigationErrorBoundary>
 * 
 * // Or use the HOC wrapper
 * export default withNavigationErrorBoundary(MyComponent);
 * 
 * @features
 * - Catches JavaScript errors in navigation-related code
 * - Provides user-friendly error UI with refresh option
 * - Logs detailed error information for debugging
 * - Prevents entire application crash due to routing issues
 * 
 * @since React Router v6 migration
 */
export class NavigationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error('Navigation error:', error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Navigation Error</h2>
          <p>There was an error with navigation. Please refresh the page.</p>
          <button onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      );
    }
    
    return this.props.children;
  }
}

/**
 * Higher-Order Component that wraps components with NavigationErrorBoundary for error handling
 * 
 * @description This HOC was created to provide a convenient way to wrap components with
 * navigation error boundary protection. It eliminates the need to manually wrap every
 * component that uses router functionality with the NavigationErrorBoundary component.
 * 
 * @why During the React Router v6 migration, many components need error boundary protection
 * to handle potential navigation errors gracefully. Rather than manually wrapping each
 * component in JSX, this HOC provides a declarative way to add error boundary protection
 * at the component definition level, similar to how withRouter adds router functionality.
 * 
 * @param {React.ComponentType} Component - The component to wrap with navigation error boundary
 * @returns {React.ComponentType} Component wrapped with NavigationErrorBoundary
 * 
 * @example
 * // Instead of manually wrapping in JSX:
 * <NavigationErrorBoundary>
 *   <MyRouterComponent />
 * </NavigationErrorBoundary>
 * 
 * // Use the HOC for cleaner component definitions:
 * const MyRouterComponent = ({ history, location }) => {
 *   // Component that uses router functionality
 *   return <div>Router component</div>;
 * };
 * 
 * export default withNavigationErrorBoundary(withRouter(MyRouterComponent));
 * 
 * @usecase Particularly useful for:
 * - Components that perform complex navigation logic
 * - Components that access route parameters or query strings  
 * - Components that programmatically navigate based on user actions
 * - Legacy components being migrated from v4/v5 to v6
 * 
 * @since React Router v6 migration
 */
export const withNavigationErrorBoundary = (Component) => {
  return (props) => (
    <NavigationErrorBoundary>
      <Component {...props} />
    </NavigationErrorBoundary>
  );
};

export default withRouter;
